import { motion, useScroll, useTransform } from "framer-motion";
import { ShieldCheck, Lock, FileText, ArrowRight, ArrowDown } from "lucide-react";
import { track } from "@/track";
import { useLang } from "@/i18n";

const HERO_IMG = "/hero-service.webp";

const container = {
  hidden: {},
  show: { transition: { staggerChildren: 0.14, delayChildren: 0.25 } },
};

const lineVariant = {
  hidden: { y: "115%" },
  show: { y: "0%", transition: { duration: 1, ease: [0.22, 1, 0.36, 1] } },
};

const fade = (delay) => ({
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.8, delay, ease: [0.22, 1, 0.36, 1] },
});

export default function Hero() {
  const { lang, t } = useLang();
  const isHi = lang === "hi";
  const { scrollY } = useScroll();
  const yImg = useTransform(scrollY, [0, 700], [0, -70]);
  const yBadgeA = useTransform(scrollY, [0, 700], [0, -130]);
  const yBadgeB = useTransform(scrollY, [0, 700], [0, -40]);
  const yGlow = useTransform(scrollY, [0, 700], [0, 140]);

  const lines = [
    { text: t("hero_l1"), brand: false },
    { text: t("hero_l2"), brand: true },
    { text: t("hero_l3"), brand: false },
  ];

  const badges = [
    { icon: ShieldCheck, label: t("hero_b1") },
    { icon: Lock, label: t("hero_b2") },
    { icon: FileText, label: t("hero_b3") },
  ];

  return (
    <section id="top" className="relative min-h-screen flex items-center pt-44 pb-24 overflow-hidden">
      <motion.div
        style={{ y: yGlow }}
        aria-hidden="true"
        className="absolute -top-40 -right-40 w-[42rem] h-[42rem] rounded-full bg-brand/10 blur-[140px]"
      />
      <div
        aria-hidden="true"
        className="absolute inset-0 opacity-[0.04]"
        style={{
          backgroundImage:
            "linear-gradient(#6366F1 1px, transparent 1px), linear-gradient(90deg, #6366F1 1px, transparent 1px)",
          backgroundSize: "80px 80px",
        }}
      />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-16 items-center w-full">
        <div className="lg:col-span-7">
          <motion.p {...fade(0.1)} className="overline-label mb-8" data-testid="hero-overline">
            {t("hero_overline")}
          </motion.p>

          <motion.h1
            variants={container}
            initial="hidden"
            animate="show"
            className={`font-serif font-semibold tracking-tight ${isHi ? "leading-[1.25]" : "leading-[0.95]"} text-5xl sm:text-6xl lg:text-7xl`}
            data-testid="hero-headline"
          >
            {lines.map((l) => (
              <span key={l.text} className={`block overflow-hidden pb-2 ${isHi ? "pt-[0.08em]" : ""}`}>
                <motion.span
                  variants={lineVariant}
                  className={`block ${l.brand ? "text-brand-300" : "text-cream"}`}
                >
                  {l.text}
                </motion.span>
              </span>
            ))}
          </motion.h1>

          <motion.p {...fade(0.9)} className="mt-8 max-w-xl text-base sm:text-lg text-cream/60 leading-relaxed" data-testid="hero-subcopy">
            {t("hero_sub")}
          </motion.p>

          <motion.div {...fade(1.05)} className="mt-10 flex flex-wrap items-center gap-5">
            <a
              href="#contact"
              data-testid="hero-get-started-btn"
              onClick={() => track("hero_cta", "get-started")}
              className="group inline-flex items-center gap-3 bg-brand text-white font-bold text-xs uppercase tracking-[0.2em] px-9 py-5 hover:bg-brand-600 transition-colors duration-300"
            >
              {t("hero_cta1")}
              <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </a>
            <a
              href="#plans"
              data-testid="hero-view-plans-btn"
              onClick={() => track("hero_cta", "view-plans")}
              className="inline-flex items-center gap-3 border border-brand/40 text-brand font-semibold text-xs uppercase tracking-[0.2em] px-9 py-5 hover:bg-brand/10 transition-colors duration-300"
            >
              {t("hero_cta2")}
            </a>
          </motion.div>

          <motion.div {...fade(1.2)} className="mt-14 flex flex-wrap gap-x-8 gap-y-3" data-testid="hero-trust-badges">
            {badges.map(({ icon: Icon, label }) => (
              <span key={label} className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.15em] text-cream/50">
                <Icon size={15} className="text-brand" />
                {label}
              </span>
            ))}
          </motion.div>
        </div>

        <div className="hidden lg:block lg:col-span-5 relative">
          <motion.div
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.2, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
            style={{ y: yImg }}
            className="relative"
          >
            <div className="absolute -inset-4 border border-brand/25 translate-x-6 translate-y-6" aria-hidden="true" />
            <div className="relative overflow-hidden" data-testid="hero-image-frame">
              <img
                src={HERO_IMG}
                alt="Professional monitoring visa appointments on a laptop with calendar and world map"
                className="w-full h-[30rem] object-cover"
                data-testid="hero-image"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-navy-900/70 via-transparent to-transparent" />
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.9, delay: 1.1 }}
            style={{ y: yBadgeA }}
            className="absolute -left-10 top-10 backdrop-blur-xl bg-navy-900/70 border border-brand/20 px-6 py-5 shadow-2xl"
            data-testid="hero-float-card-monitoring"
          >
            <p className="font-serif text-3xl text-brand leading-none">{t("hero_cardA_big")}</p>
            <p className="mt-2 text-[10px] uppercase tracking-[0.2em] text-cream/60">{t("hero_cardA_small")}</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.9, delay: 1.3 }}
            style={{ y: yBadgeB }}
            className="absolute -right-4 bottom-8 backdrop-blur-xl bg-navy-900/70 border border-brand/20 px-6 py-5 shadow-2xl"
            data-testid="hero-float-card-booking"
          >
            <p className="font-serif text-3xl text-cream leading-none">{t("hero_cardB_big")}</p>
            <p className="font-serif text-3xl text-brand-300 leading-none">{t("hero_cardB_big2")}</p>
            <p className="mt-2 text-[10px] uppercase tracking-[0.2em] text-cream/60">{t("hero_cardB_small")}</p>
          </motion.div>
        </div>
      </div>

      <motion.a
        href="#why-us"
        data-testid="hero-scroll-indicator"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 text-brand/60 hover:text-brand transition-colors"
        aria-label="Scroll to next section"
      >
        <motion.span animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 2 }} className="block">
          <ArrowDown size={20} />
        </motion.span>
      </motion.a>
    </section>
  );
}
